import React, { useState } from 'react';
import { Table, Button, Badge, ButtonGroup } from 'react-bootstrap';
import ConfirmDialog from './ConfirmDialog';
import { User } from '../../models/user';

interface UserTableProps {
  users: User[];
  onUpdateUser: (user: User) => void;
  onDeleteUser: (userId: number) => void;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onUpdateUser,
  onDeleteUser,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogType, setDialogType] = useState<'edit' | 'delete'>('edit');
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // 編集ボタンの処理
  const handleEditClick = (user: User): void => {
    setSelectedUser(user);
    setDialogType('edit');
    setShowDialog(true);
  };

  // 削除ボタンの処理
  const handleDeleteClick = (user: User): void => {
    setSelectedUser(user);
    setDialogType('delete');
    setShowDialog(true);
  };

  // ダイアログの結果を受け取る
  const handleDialogConfirm = (updatedUser: User): void => {
    if (dialogType === 'edit') {
      onUpdateUser(updatedUser);
    } else if (dialogType === 'delete' && selectedUser) {
      onDeleteUser(selectedUser.id);
    }
    setShowDialog(false);
  };

  return (
    <>
      <div className="table-responsive">
        <Table className="user-table">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>
                <i className="fas fa-id-card"></i>ID
              </th>
              <th style={{ width: '280px' }}>
                <i className="fas fa-user"></i>ユーザー情報
              </th>
              <th style={{ width: '300px' }}>
                <i className="fas fa-envelope"></i>メールアドレス
              </th>
              <th style={{ width: '140px' }}>
                <i className="fas fa-toggle-on"></i>ステータス
              </th>
              <th style={{ width: '180px' }}>
                <i className="fas fa-cogs"></i>操作
              </th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr
                  key={user.id}
                  className={index % 2 === 0 ? 'table-row-even' : ''}
                >
                  <td className="text-center">
                    <Badge className="user-id-badge">{user.id}</Badge>
                  </td>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        <i className="fas fa-user-circle"></i>
                      </div>
                      <div className="user-details">
                        <div className="username">{user.username}</div>
                        <small>管理ユーザー</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="email-info">
                      <i className="fas fa-at"></i>
                      <span className="email-text">{user.email}</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <Badge
                      bg={user.isActive ? 'success' : 'secondary'}
                      className="status-badge"
                    >
                      <i
                        className={`fas ${user.isActive ? 'fa-check-circle' : 'fa-pause-circle'}`}
                      ></i>
                      {user.isActive ? 'アクティブ' : '停止中'}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <ButtonGroup size="sm" className="action-buttons">
                      <Button
                        variant="outline-primary"
                        onClick={() => handleEditClick(user)}
                        className="action-btn edit-btn"
                        title="ユーザー情報を編集"
                      >
                        <i className="fas fa-edit"></i>
                        <span className="btn-text">編集</span>
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteClick(user)}
                        className="action-btn delete-btn"
                        title="ユーザーを削除"
                      >
                        <i className="fas fa-trash"></i>
                        <span className="btn-text">削除</span>
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">
                  <div className="no-data">
                    <i className="fas fa-users fa-3x"></i>
                    <h5>ユーザーデータがありません</h5>
                    <p>
                      ユーザー一覧を表示するには、上の「ユーザー一覧取得」ボタンをクリックしてください。
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* 共通ダイアログの呼び出し */}
      <ConfirmDialog
        show={showDialog}
        type={dialogType}
        user={selectedUser}
        onConfirm={handleDialogConfirm}
        onCancel={() => setShowDialog(false)}
      />
    </>
  );
};

export default UserTable;
